/**
 * L3A — CMS operator terminology catalog (fa-AF).
 *
 * Semantic keys only — never use Dari text as an application key.
 * Source of truth for approved static chrome / nav / shared display labels.
 * Page-local editor/form copy migrates in later L3/L4 slices.
 *
 * @see docs/CMS-TERMINOLOGY-ARCHITECTURE-L3A.md
 * @see docs/CMS-AFGHAN-DARI-GLOSSARY-v1.md
 * @see docs/CMS-NAVIGATION-AND-TITLE-MAP-v1.md
 */

export const cmsStrings = {
  brand: {
    /** English brand — keep LTR. */
    nameEn: "Tofan Portal",
    productTitle: "مدیریت محتوای پورتال",
  },

  shell: {
    skipToContent: "پرش به محتوا",
    mainMenuAria: "منوی اصلی",
    menuSearchPlaceholder: "جستجوی منو…",
    menuFilterAria: "فیلتر منو",
    systemSearchPlaceholder: "جستجو در سیستم…",
    systemSearchAria: "جستجوی سراسری سیستم",
    systemSearchTrigger: "جستجو",
    systemSearchShortcut: "Ctrl+K",
    systemSearchOpenAria: "باز کردن جستجوی سیستم",
    systemSearchResultsAria: "نتایج جستجو",
    systemSearchEmpty: "موردی یافت نشد.",
    systemSearchHint: "مقصدهای CMS",
    systemSearchRecords: "سوابق",
    closeMenu: "بستن منو",
    openMenu: "منو",
    openMenuAria: "باز کردن منو",
    logout: "خروج",
    breadcrumbAria: "مسیر صفحه",
    loading: "در حال بارگذاری…",
    unavailable: "در دسترس نیست",
    displayTimezoneLabel: "منطقهٔ زمانی نمایش:",
    /** Data literal — Asia/Kabul stays Latin/LTR. */
    displayTimezoneValue: "Asia/Kabul",
    envDevBadge: "محیط توسعه",
    orgContextAria: "زمینهٔ سازمان",
    passwordConfirmLabel: "رمز عبور برای تأیید",
    cancel: "انصراف",
  },

  roles: {
    owner: "مالک",
    publisher: "ناشر",
    editor: "ویرایشگر",
    auditor: "ممیز",
    tenant_admin: "مدیر سازمان",
  },

  groups: {
    overview: "نمای کلی",
    content: "محتوا",
    network: "محدوده شبکه",
    release: "انتشارها",
    operations: "عملیات",
    administration: "مدیریت",
  },

  nav: {
    overview: "نمای کلی",
    announcements: "اطلاعیه‌ها",
    promotions: "پیشنهادها",
    sponsored: "تبلیغات ویژه",
    sitesAndGroups: "سایت‌ها و گروه‌ها",
    resellerProfiles: "پروفایل‌های نماینده",
    /** English technical — Bundle. */
    bundle: "Bundle",
    fleet: "ناوگان و استقرار",
    drift: "ناهماهنگی تنظیمات",
    versions: "تاریخچه انتشار",
    observability: "پایش عملیات",
    alerts: "هشدارها",
    activity: "فعالیت",
    backupRestore: "Backup و آزمایش بازگردانی",
    memberships: "اعضای سازمان",
    organizations: "حساب‌های سازمان",
    entitlements: "دسترسی پلان",
    users: "کاربران و نقش‌ها",
    settings: "تنظیمات",
    audit: "سابقه تغییرات",
  },

  pages: {
    overview: {
      title: "نمای کلی",
      explanation: "وضعیت کلی، هشدارها و میانبرها",
    },
    announcements: {
      title: "اطلاعیه‌ها",
      explanation: "پیام‌های قابل نمایش برای کاربران هات‌اسپات",
    },
    promotions: {
      title: "پیشنهادها",
      explanation: "پیشنهادهای خود سازمان",
    },
    sponsored: {
      title: "تبلیغات ویژه",
      explanation: "جایگاه پولی آگهی حمایت‌شده",
    },
    sites: {
      title: "سایت‌ها و گروه‌ها",
      explanation: "محل‌های هات‌اسپات و گروه‌های عملیاتی",
    },
    resellerProfiles: {
      title: "پروفایل‌های نماینده",
      explanation: "برندینگ مشترک چند سایت",
    },
    portalBundles: {
      title: "Bundle",
      explanation: "فایل نصب سایت (ZIP) از الگوی تأییدشده",
    },
    fleet: {
      title: "ناوگان و استقرار",
      explanation: "موجودی روتر و ثبت نصب دستی",
    },
    fleetDrift: {
      title: "ناهماهنگی تنظیمات",
      explanation: "سایت یا روتر با نسخهٔ انتظار هم‌خوان نیست",
    },
    fleetInventory: {
      title: "ناوگان و استقرار",
      explanation: "موجودی، حلقه Pilot / Canary و تولید",
    },
    fleetDeployments: {
      title: "ناوگان و استقرار",
      explanation: "ثبت قصد و نصب دستی",
    },
    versions: {
      title: "تاریخچه انتشار",
      explanation: "نسخه‌های نشر و بازگردانی",
    },
    observability: {
      title: "پایش عملیات",
      explanation: "سلامت CMS، شمارنده‌ها و حریم خصوصی",
    },
    observabilityAlerts: {
      title: "هشدارها",
      explanation: "موارد نیازمند توجه",
    },
    observabilityBackup: {
      title: "Backup و آزمایش بازگردانی",
      explanation: "سن Backup و تمرین ایزوله",
    },
    observabilityAggregates: {
      title: "پایش عملیات",
      explanation: "شمارنده‌های محتوا",
    },
    activity: {
      title: "فعالیت",
      explanation: "رویدادهای اخیر سازمان",
    },
    memberships: {
      title: "اعضای سازمان",
      explanation: "دعوت و نقش داخل سازمان",
    },
    organizations: {
      title: "حساب‌های سازمان",
      explanation: "ایجاد و فهرست حساب‌های سازمان",
    },
    entitlements: {
      title: "دسترسی پلان",
      explanation: "سقف‌ها و امکانات این سازمان",
    },
    users: {
      title: "کاربران و نقش‌ها",
      explanation: "حساب‌های ورود به CMS",
    },
    settings: {
      title: "تنظیمات",
      explanation: "فهرست CTA و قطع فوری محتوای CMS",
    },
    audit: {
      title: "سابقه تغییرات",
      explanation: "چه کسی چه چیز را کی تغییر داد",
    },
  },

  organizationsUi: {
    createCta: "ایجاد حساب",
    saveCta: "ثبت حساب",
    saving: "در حال ذخیره…",
    created: "حساب سازمان ایجاد شد.",
    switched: "سازمان فعال تغییر کرد.",
    createFailed: "ایجاد ناموفق",
    switchFailed: "تغییر سازمان ناموفق",
    loadFailed: "بارگذاری ناموفق",
    noAccess: "فقط مالک پلتفرم می‌تواند حساب سازمان بسازد.",
    safetyEyebrow: "حساب سازمان",
    safetyOwner: "ایجاد حساب فقط برای مالک پلتفرم است",
    safetyDefault: "حساب جدید با وضعیت فعال و پلان نامحدود ساخته می‌شود",
    safetyLifecycle: "تعلیق و بایگانی در این صفحه نیست",
    safetyDisclosure: "متن کامل حریم حساب سازمان",
    metricAll: "همه",
    metricActive: "فعال",
    metricOther: "سایر وضعیت‌ها",
    metricsAria: "شمارش حساب‌های سازمان",
    listTitle: "حساب‌ها",
    listDescription:
      "فهرست همهٔ حساب‌ها — نه فقط سازمان فعال. جستجو و فیلتر روی داده‌های بارگذاری‌شده.",
    emptyTitle: "حسابی ثبت نشده است",
    composerTitle: "ایجاد حساب سازمان",
    searchPlaceholder: "نام یا کد…",
    displayName: "نام نمایشی",
    operationalCode: "کد عملیاتی",
    inspectTitle: "جزئیات حساب",
    closeInspect: "بستن",
    inspectKind: "حساب‌های سازمان",
    inspectActiveSuffix: " (سازمان فعال)",
    tableActiveSuffix: " (فعال)",
    colName: "نام",
    colCode: "کد",
    colStatus: "وضعیت",
    factCode: "کد عملیاتی",
    factCreated: "ایجاد",
    factUpdated: "به‌روزرسانی",
    relatedAria: "مسیرهای مرتبط سازمان",
    tableAria: "جدول حساب‌های سازمان",
    cardsAria: "کارت‌های حساب‌های سازمان",
    filterAria: "فیلتر حساب‌های سازمان",
    searchAria: "جستجوی حساب سازمان",
    sortAria: "مرتب‌سازی فهرست حساب‌های سازمان",
    sortName: "نام",
    sortCode: "کد",
    sortStatus: "وضعیت",
    switchCta: "انتخاب به‌عنوان سازمان فعال",
    alreadyActive: "سازمان فعال",
    statusDraft: "پیش‌نویس",
    statusActive: "فعال",
    statusSuspended: "معلق",
    statusArchived: "بایگانی‌شده",
  },

  killSwitch: {
    /** Approved product label — full remote CMS content scope (not sponsored-only). */
    title: "قطع فوری محتوای CMS",
    /** Deprecated — must not appear in operator UI. */
    deprecatedSponsoredOnlyTitle: "قطع فوری تبلیغات ویژه",
    scopeNote:
      "هنگام فعال بودن، محتوای راه دور CMS در پاسخ عمومی مخفی می‌شود: اطلاعیه‌ها، پیشنهادها، و تبلیغات ویژه. ورود کاربران و CHAP دست‌نخورده می‌مانند؛ دارایی محلی پورتال و RouterOS تغییر نمی‌کنند.",
    platformSectionDescription:
      "سراسری پلتفرم — فقط مالک پلتفرم با تأیید رمز. محتوای راه دور را مخفی می‌کند؛ ورود و CHAP دست‌نخورده می‌مانند.",
    healthHint: "فقط نمایش محتوا — ورود/CHAP دست‌نخورده",
    activeAttention:
      "قطع فوری محتوای CMS فعال است — اطلاعیه‌ها، پیشنهادها و تبلیغات ویژهٔ راه دور مخفی‌اند.",
    activeAlertLabel: "قطع فوری محتوای CMS فعال",
    loadingStatus: "در حال بارگذاری وضعیت قطع فوری…",
    statusAria: "وضعیت قطع فوری",
    affectedContentAria: "محتوای تحت تأثیر",
  },

  /** Shared inventory chrome (content lists + reusable empties). L3B. */
  inventory: {
    filterStatusAria: "فیلتر وضعیت",
    filterAll: "همه",
    filterQueue: "صف تأیید",
    filterPaused: "متوقف‌شده",
    sortLabel: "مرتب‌سازی",
    sortUpdated: "به‌روزرسانی",
    sortTitle: "عنوان",
    sortSeverity: "شدت",
    sortCta: "CTA",
    sortCampaignName: "نام کمپین",
    sortAdvertiser: "تبلیغ‌کننده",
    sortPriority: "اولویت",
    details: "جزئیات",
    relatedContentAria: "مسیرهای مرتبط محتوا",
    defaultListDescription:
      "جستجو و فیلتر روی داده‌های بارگذاری‌شده — فیلتر پیش‌فرض: همه",
    queueCountLabel: "مورد در صف تأیید",
    noMatchTitle: "موردی در این فیلتر نیست",
    noMatchHintStatus: "عبارت جستجو یا فیلتر وضعیت را تغییر دهید.",
    factId: "شناسه",
    factTargeting: "هدف‌گذاری",
    factSchedule: "بازه (کابل)",
    factPriority: "اولویت",
    factUpdated: "آخرین به‌روزرسانی",
    emergencyBadge: "اضطراری",
  },

  overviewUi: {
    pageSubtitle: "وضعیت عملیاتی سازمان، هشدارها و فعالیت اخیر",
    attentionAria: "موارد نیازمند توجه",
    dismissAria: "بستن",
    introChecklistAria: "شروع کار",
    introChecklistTitle: "شروع کار",
    introChecklistOrg: "سازمان فعال را در نوار بالا تأیید کنید.",
    introChecklistContent: "اطلاعیه‌ها یا پیشنهادها را باز کنید.",
    introChecklistSites: "سایت‌ها و گروه‌ها را باز کنید.",
    introChecklistAudit: "سابقه تغییرات را باز کنید.",
    introChecklistSettings: "قطع فوری محتوای CMS در تنظیمات است.",
    introChecklistDismiss: "پنهان کردن",
    healthTitle: "سلامت و عملیات",
    healthDescAllowed: "سیگنال‌های موجود از پایش CMS — بدون دادهٔ ساختگی",
    healthDescDenied: "برای دیدن سلامت سیستم به مجوز audit.read نیاز است",
    healthDeniedTitle: "سلامت سیستم در این نقش در دسترس نیست",
    healthDeniedDescription:
      "خلاصهٔ آمادگی، Backup و ناهماهنگی تنظیمات فقط با مجوز مشاهدهٔ عملیات نمایش داده می‌شود.",
    readinessLabel: "آمادگی CMS",
    backupAgeLabel: "سن Backup",
    backupAgeHint: "از آخرین فایل vital-portal-*.db",
    driftHint: "ثبت اپراتور — بدون پروب زندهٔ روتر",
    contentStatusAria: "وضعیت محتوا",
    contentStatusTitle: "وضعیت محتوا",
    contentStatusDescription: "شمارش از خلاصهٔ داشبورد فعلی سازمان",
    draftsLabel: "پیش‌نویس‌ها",
    draftsSublabel: "در انتظار نشر",
    publishedOrScheduled: "منتشر یا زمان‌بندی‌شده",
    scheduledLabel: "زمان‌بندی‌شده",
    scheduledSublabel: "همهٔ انواع محتوا",
    alertsTitle: "هشدارها",
    alertsDescription: "موارد نیازمند توجه",
    alertsAction: "مشاهدهٔ هشدارها",
    alertsDeniedTitle: "هشدارها در این نقش در دسترس نیست",
    alertsDeniedDescription: "برای مشاهدهٔ سیگنال‌های عملیاتی مجوز audit.read لازم است.",
    alertsPlatformOnlyTitle: "تاریخچهٔ هشدار فقط برای نقش‌های سکو",
    alertsPlatformOnlyDescription:
      "خلاصهٔ سلامت بالا برای سازمان شما در دسترس است. فهرست رویدادهای هشدار برای مالک/اپراتور سکوست.",
    alertsLoadFailedTitle: "بارگذاری هشدارها ممکن نشد",
    alertsLoadFailedDescription:
      "دادهٔ هشدار در این لحظه در دسترس نیست — بعداً از پایش عملیات تازه کنید.",
    alertsEmptyTitle: "هشدار فعالی ثبت نشده است",
    alertsEmptyDescription: "وقتی هشداری شلیک شود اینجا دیده می‌شود.",
    opsLinksTitle: "پیوندهای عملیاتی",
    opsLinksDescription: "میانبر به مسیرهای موجود",
    activityTitle: "فعالیت",
    activityDescription: "رویدادهای اخیر سازمان",
    viewAll: "مشاهدهٔ همه",
    createAnnouncement: "ایجاد اطلاعیه",
    createPromotion: "ایجاد پیشنهاد",
    attentionDrafts: "پیش‌نویس‌هایی در انتظار پیگیری هستند.",
    attentionReadiness: "آمادگی CMS نیاز به بررسی دارد.",
    attentionBackup: "سن Backup از حد مجاز گذشته است.",
    attentionDrift: "ناهماهنگی تنظیمات در ناوگان ثبت شده است.",
    attentionAlerts: "هشدار عملیاتی ثبت شده است.",
    attentionSitesQuota: "سایت‌های فعال نزدیک سقف دسترسی پلان هستند.",
    attentionMembersQuota: "اعضای فعال نزدیک سقف دسترسی پلان هستند.",
    attentionStorageQuota: "فضای فایل نزدیک سقف دسترسی پلان است.",
    attentionPublishOff: "نشر محتوا برای این سازمان غیرفعال است.",
    attentionBundleOff: "تولید Bundle برای این سازمان غیرفعال است.",
    attentionSponsoredOff: "تبلیغات ویژه برای این سازمان غیرفعال است.",
    sponsoredLinkHint: "مدیریت کمپین‌های حمایت‌شده",
  },

  announcementsUi: {
    pageSubtitle: "مرور عملیاتی اطلاعیه‌ها — متمایز از پیشنهادها و تبلیغات ویژه",
    listTitle: "فهرست اطلاعیه‌ها",
    loadingDescription: "در حال دریافت اطلاعیه‌ها",
    emptyTitle: "اطلاعیه‌ای ثبت نشده است",
    emptyDescription:
      "با فرم ایجاد پیش‌نویس در پایین صفحه یک اطلاعیه بسازید. این صفحه پیشنهادها یا تبلیغات ویژه نیست.",
    emptyDescriptionReadonly: "اطلاعیه‌ای برای این سازمان ثبت نشده است.",
    inspectTitle: "جزئیات اطلاعیه",
    inspectDescription: "خلاصهٔ فیلدهای موجود — بدون دادهٔ ساختگی",
    searchPlaceholder: "عنوان، شدت، هدف، SITE_CODE…",
    searchAria: "جستجوی اطلاعیه",
    sortAria: "مرتب‌سازی فهرست اطلاعیه‌ها",
    statusAria: "وضعیت اطلاعیه",
    tableAria: "جدول اطلاعیه‌ها",
    cardsAria: "کارت‌های اطلاعیه‌ها",
    colStatus: "وضعیت",
    colTarget: "هدف",
    factEligibility: "نمایش عمومی",
    factSubmitted: "ارسال برای تأیید",
    factPausedFrom: "توقف از",
  },

  /**
   * Announcements composer + lifecycle chrome (L4A).
   * Plain Dari for operators; technical values stay EN/LTR in the form.
   */
  announcementsComposer: {
    createDraftCta: "ایجاد پیش‌نویس",
    cardTitle: "ایجاد پیش‌نویس",
    cardDescription:
      "هدف‌گذاری سایت، گروه سایت‌ها یا سراسر سازمان — زمان‌بندی اختیاری بر اساس Asia/Kabul",
    titleLabel: "عنوان",
    bodyLabel: "متن",
    charsUnit: "کاراکتر",
    severityLabel: "شدت",
    dismissible: "قابل بستن توسط کاربر",
    emergencyLegend: "اضطراری (L1)",
    emergencyHintBefore: "حداکثر",
    emergencyHintAfter: "ساعت؛ قابل بستن غیرفعال می‌شود؛ زمان پایان الزامی است.",
    emergencyCheckbox: "اطلاعیه اضطراری",
    emergencyAudience: "دامنه اضطراری",
    emergencyOrgOnly: "فقط این سازمان",
    emergencyPlatform: "سراسر پلتفرم",
    saveDraft: "ذخیره پیش‌نویس",
    saving: "در حال ذخیره…",
    previewTitle: "پیش‌نمایش پورتال",
    previewDescription: "جای اطلاعیه روی صفحه ورود هات‌اسپات — عرض واقعی مهمان ۳۲۰px",
    previewAria: "پیش‌نمایش صفحه ورود هات‌اسپات",
    previewViewportHint: "عرض مهمان هات‌اسپات",
    previewTagline: "خدمات اینترنت مطمئن و سریع",
    previewWelcome: "خوش آمدید",
    previewLoginHint: "برای وصل شدن به اینترنت، نام کاربری و رمز عبور خود را وارد کنید.",
    previewUsername: "نام کاربری",
    previewPassword: "رمز عبور",
    previewLoginSubmit: "ورود",
    previewEmptyTitle: "عنوان اطلاعیه",
    previewEmptyBody: "متن اطلاعیه اینجا نمایش داده می‌شود.",
    scheduleLegend: "بازهٔ زمانی (اختیاری)",
    scheduleHintBefore: "زمان‌ها بر اساس",
    scheduleHintAfter: "وارد می‌شوند و به UTC ذخیره می‌گردند. اگر شروع در آینده باشد، اطلاعیه زمان‌بندی‌شده خواهد بود.",
    compare: "مقایسه با آخرین نشر",
    submitForApproval: "ارسال برای تأیید",
    publish: "نشر کردن",
    publishConfirmTitle: "تأیید نشر",
    publishConfirmLabel: "نشر نهایی",
    publishImpact:
      "پس از تأیید رمز، این اطلاعیه برای هدف و بازهٔ زمانی تنظیم‌شده در صفحه هات‌اسپات نشان داده می‌شود (الان یا طبق زمان‌بندی).",
    publishEmergencyImpact:
      "اطلاعیه اضطراری با اولویت L1 منتشر می‌شود (حداکثر ۲۴ ساعت). ورود کاربران و CHAP تغییر نمی‌کند.",
    deleteDraft: "حذف پیش‌نویس",
    reject: "رد کردن",
    approveAndPublish: "تأیید و نشر",
    pause: "توقف موقت",
    pauseConfirmTitle: "تأیید توقف موقت",
    pauseConfirmLabel: "توقف موقت",
    pauseImpact:
      "نمایش این اطلاعیه موقتاً مخفی می‌شود؛ محتوا نگه داشته می‌شود و می‌توان دوباره آن را نشان داد.",
    archive: "بایگانی کردن",
    archiveConfirmTitle: "تأیید بایگانی",
    archiveConfirmLabel: "بایگانی کردن",
    archiveImpact:
      "این اطلاعیه دیگر در صفحه هات‌اسپات نشان داده نمی‌شود؛ برای سابقه نگه داشته می‌شود.",
    resume: "ادامهٔ نمایش",
    resumeConfirmTitle: "تأیید ادامهٔ نمایش",
    resumeConfirmLabel: "ادامهٔ نمایش",
    resumeImpact:
      "اطلاعیه دوباره طبق هدف و بازهٔ زمانی فعلی در هات‌اسپات نشان داده می‌شود.",
    summaryTitle: "عنوان",
    summaryTarget: "هدف",
    rejectDialogTitle: "رد درخواست تأیید",
    rejectDialogBody:
      "به پیش‌نویس برمی‌گردد. دلیل رد در سابقه تغییرات ثبت می‌شود.",
    rejectReason: "دلیل رد",
    cancel: "انصراف",
    rejecting: "در حال رد…",
    diffTitlePrefix: "مقایسه با آخرین نشر",
    close: "بستن",
    emergencyNeedsEnd:
      "اطلاعیه اضطراری نیازمند زمان پایان است (حداکثر ۲۴ ساعت).",
    draftSaved: "پیش‌نویس ذخیره شد.",
    emergencyDraftSaved: "پیش‌نویس اضطراری ذخیره شد.",
  },

  /** Shared targeting field labels (optional overrides; L4A–L4C content composers). */
  contentTargeting: {
    legend: "هدف‌گذاری",
    scope: "دامنه",
    site: "سایت هدف",
    group: "گروه سایت‌ها",
    selectGroup: "انتخاب گروه…",
    groupEmptyHint: "ابتدا در «سایت‌ها و گروه‌ها» یک گروه بسازید.",
    globalHint: "برای همهٔ سایت‌های فعال این سازمان نمایش داده می‌شود.",
    priority: "اولویت (۰–۱۰۰۰)",
  },

  /**
   * Promotions composer + lifecycle chrome (L4B).
   * CTA allowlist + image type/size rules stay enforced by existing validation/API.
   */
  promotionsComposer: {
    createDraftCta: "ایجاد پیش‌نویس",
    cardTitleCreate: "ایجاد پیش‌نویس",
    cardTitleEdit: "ویرایش پیش‌نویس",
    cardDescription: "متن ساده فقط — بدون HTML یا URL آزاد",
    titleLabel: "عنوان",
    bodyLabel: "متن",
    badgeLabel: "نشان",
    badgeNone: "بدون نشان",
    badgeHint: "برچسب کوچک روی کارت پیشنهاد — در پیش‌نمایش کنار عنوان دیده می‌شود.",
    optionalSuffix: "اختیاری",
    charsUnit: "کاراکتر",
    ctaDestination: "مقصد CTA",
    ctaNone: "بدون CTA",
    ctaApprovedUrl: "URL تأییدشده",
    ctaSelectUrl: "انتخاب کنید…",
    ctaAllowlistEmpty:
      "فقط URLهای HTTPS تأییدشده از فهرست CTA. هنوز موردی ثبت نشده (تنها مالک).",
    ctaAllowlistHint:
      "مقصد سفارشی باید از فهرست CTA تأییدشدهٔ HTTPS انتخاب شود؛ URL آزاد مجاز نیست.",
    ctaButtonLabel: "برچسب دکمه",
    imageLabelBefore: "تصویر — اختیاری (JPEG/PNG/WebP، حداکثر",
    imageLabelAfter: "KB)",
    imagePick: "فایل را انتخاب کنید",
    imageUploading: "در حال بارگذاری…",
    imageAltLabel: "متن جایگزین تصویر",
    imageClear: "حذف تصویر",
    saveDraft: "ذخیره پیش‌نویس",
    updateDraft: "به‌روزرسانی",
    saving: "در حال ذخیره…",
    cancel: "انصراف",
    previewTitle: "پیش‌نمایش پورتال",
    previewDescription: "جای کارت پیشنهاد روی صفحه ورود هات‌اسپات — عرض واقعی مهمان ۳۲۰px",
    previewAria: "پیش‌نمایش صفحه ورود هات‌اسپات",
    previewViewportHint: "عرض مهمان هات‌اسپات",
    previewTagline: "خدمات اینترنت مطمئن و سریع",
    previewWelcome: "خوش آمدید",
    previewLoginHint: "برای وصل شدن به اینترنت، نام کاربری و رمز عبور خود را وارد کنید.",
    previewUsername: "نام کاربری",
    previewPassword: "رمز عبور",
    previewLoginSubmit: "ورود",
    previewEmptyTitle: "عنوان پیشنهاد",
    previewEmptyBody: "متن پیشنهاد اینجا نمایش داده می‌شود.",
    scheduleLegend: "بازهٔ زمانی (اختیاری)",
    scheduleHintBefore: "زمان‌ها بر اساس",
    scheduleHintAfter:
      "وارد می‌شوند و به UTC ذخیره می‌گردند. اگر شروع در آینده باشد، پیشنهاد زمان‌بندی‌شده خواهد بود.",
    compare: "مقایسه با آخرین نشر",
    edit: "ویرایش",
    submitForApproval: "ارسال برای تأیید",
    publish: "نشر کردن",
    publishConfirmTitle: "تأیید نشر",
    publishConfirmLabel: "نشر نهایی",
    publishImpact:
      "پس از تأیید رمز، این پیشنهاد برای هدف و بازهٔ زمانی تنظیم‌شده در صفحه هات‌اسپات نشان داده می‌شود (الان یا طبق زمان‌بندی). CTA فقط از فهرست HTTPS تأییدشده و تصویر فقط با قواعد فعلی بارگذاری مجاز است.",
    approveAndPublish: "تأیید و نشر",
    reject: "رد",
    pause: "توقف موقت",
    pauseConfirmTitle: "تأیید توقف موقت",
    pauseConfirmLabel: "توقف موقت",
    pauseImpact:
      "نمایش این پیشنهاد موقتاً مخفی می‌شود؛ محتوا نگه داشته می‌شود و می‌توان دوباره آن را نشان داد.",
    archive: "بایگانی کردن",
    archiveConfirmTitle: "تأیید بایگانی",
    archiveConfirmLabel: "بایگانی کردن",
    archiveImpact:
      "این پیشنهاد دیگر در صفحه هات‌اسپات نشان داده نمی‌شود؛ برای سابقه نگه داشته می‌شود.",
    resume: "ادامهٔ نمایش",
    resumeConfirmTitle: "تأیید ادامهٔ نمایش",
    resumeConfirmLabel: "ادامهٔ نمایش",
    resumeImpact:
      "پیشنهاد دوباره طبق هدف و بازهٔ زمانی فعلی در هات‌اسپات نشان داده می‌شود.",
    history: "تاریخچه",
    historyTitle: "تاریخچهٔ نشر",
    historyEmpty: "نسخهٔ منتشرشده‌ای یافت نشد.",
    historyVersion: "نسخه",
    summaryTitle: "عنوان",
    summaryTarget: "هدف",
    summarySchedule: "بازه",
    summaryCta: "CTA",
    rejectDialogTitle: "رد درخواست تأیید",
    rejectDialogBody:
      "به پیش‌نویس برمی‌گردد. دلیل رد در سابقه تغییرات ثبت می‌شود.",
    rejectReason: "دلیل رد",
    rejecting: "در حال رد…",
    rejectConfirm: "رد کردن",
    diffTitlePrefix: "مقایسه با آخرین نشر",
    close: "بستن",
    loadingInline: "در حال بارگذاری…",
    draftSaved: "پیش‌نویس ذخیره شد.",
    draftUpdated: "پیش‌نویس به‌روزرسانی شد.",
  },

  promotionsUi: {
    pageSubtitle: "مرور عملیاتی پیشنهادها — متمایز از اطلاعیه‌ها و تبلیغات ویژه",
    listTitle: "فهرست پیشنهادها",
    loadingDescription: "در حال دریافت پیشنهادها",
    emptyTitle: "پیشنهادی ثبت نشده است",
    emptyDescription:
      "با فرم ایجاد پیش‌نویس در پایین صفحه یک پیشنهاد بسازید. این صفحه اطلاعیه‌ها یا تبلیغات ویژه نیست.",
    emptyDescriptionReadonly: "پیشنهادی برای این سازمان ثبت نشده است.",
    inspectTitle: "جزئیات پیشنهاد",
    inspectDescription: "خلاصهٔ فیلدهای موجود — بدون دادهٔ ساختگی",
    searchPlaceholder: "عنوان، CTA، هدف، SITE_CODE…",
    searchAria: "جستجوی پیشنهاد",
    sortAria: "مرتب‌سازی فهرست پیشنهادها",
    statusAria: "وضعیت پیشنهاد",
    tableAria: "جدول پیشنهادها",
    cardsAria: "کارت‌های پیشنهادها",
    colStatus: "وضعیت",
    colTarget: "هدف",
    factBadge: "نشان",
    factCta: "CTA",
    factCtaUrl: "آدرس CTA",
    factImagePath: "مسیر تصویر",
    factEligibility: "نمایش عمومی",
    factSubmitted: "ارسال برای تأیید",
    factPausedFrom: "توقف از",
    inspectSectionPlacement: "هدف و زمان",
    inspectSectionAudit: "سابقه",
  },

  /**
   * Sponsored (تبلیغات ویژه) advertiser/campaign composer + lifecycle (L4C).
   * Entitlement, one-creative, CTA allowlist, duration, and image limits stay API-enforced.
   */
  sponsoredComposer: {
    createDraftCta: "ایجاد پیش‌نویس",
    entitlementDenied:
      "دسترسی پلان این سازمان تبلیغات ویژه را مجاز نمی‌کند. ایجاد یا نشر کمپین ممکن نیست. شمارش نمایش، CPC/CPM، یا ورود جداگانهٔ تبلیغ‌کننده در این صفحه نیست.",
    advertisersTitle: "تبلیغ‌کنندگان",
    advertisersDescription:
      "فقط برای کارکنان سازمان — در خروجی عمومی نمی‌آید به‌جز نام نمایشی روی آگهی. ورود جداگانهٔ تبلیغ‌کننده وجود ندارد.",
    advertiserName: "نام نمایشی",
    advertiserNotes: "یادداشت داخلی — فقط ادمین",
    advertiserSave: "ثبت تبلیغ‌کننده",
    advertiserSaved: "تبلیغ‌کننده ثبت شد.",
    advertiserStatusActive: "فعال",
    advertiserStatusArchived: "بایگانی",
    advertiserNotesPrefix: "یادداشت داخلی:",
    advertiserArchive: "بایگانی کردن",
    advertiserArchivedBlocked: "این تبلیغ‌کننده بایگانی شده و نشر ممکن نیست.",
    cardTitleCreate: "ایجاد پیش‌نویس آگهی",
    cardTitleEdit: "ویرایش پیش‌نویس آگهی",
    cardDescription:
      "متن ساده فقط — یک تصویر JPEG/PNG/WebP الزامی؛ CTA فقط از فهرست HTTPS تأییدشده؛ بدون شمارش نمایش یا CPC/CPM.",
    oneCreativeHint: "هر کمپین فقط یک تصویر خلاقانه دارد؛ جایگزینی تصویر همان رکورد را به‌روز می‌کند.",
    advertiserLabel: "تبلیغ‌کننده",
    selectPlaceholder: "انتخاب کنید…",
    campaignName: "نام داخلی کمپین",
    sponsorshipLabel: "برچسب آگهی حمایت‌شده",
    sponsorshipLabelHintBefore: "پیش‌فرض",
    sponsorshipLabelHintAfter: "— پنهان‌کردنی نیست.",
    headline: "عنوان آگهی",
    description: "توضیح کوتاه",
    offerText: "متن پیشنهاد / قیمت",
    optionalSuffix: "اختیاری",
    charsUnit: "کاراکتر",
    ctaDestination: "مقصد CTA",
    ctaRequiredSuffix: "الزامی",
    ctaApprovedUrl: "URL تأییدشده",
    ctaAllowlistEmpty:
      "فقط URLهای HTTPS تأییدشده از فهرست CTA. هنوز موردی ثبت نشده (تنها مالک).",
    ctaAllowlistHint:
      "CTA سفارشی باید از فهرست CTA تأییدشدهٔ HTTPS انتخاب شود؛ URL آزاد مجاز نیست.",
    ctaButtonLabel: "برچسب دکمه",
    imageLabelBefore: "تصویر — الزامی (JPEG/PNG/WebP، حداکثر",
    imageLabelMid: "KB، ضلع حداکثر",
    imageLabelAfter: "px)",
    imagePick: "فایل را انتخاب کنید",
    imageUploading: "در حال بارگذاری…",
    imageAltLabel: "متن جایگزین تصویر",
    imageClear: "حذف تصویر",
    scheduleLegend: "بازهٔ زمانی (الزامی)",
    scheduleHintBefore: "هر دو زمان الزامی است؛ حداکثر بازه",
    scheduleHintMid: "روز. زمان‌ها بر اساس",
    scheduleHintAfter: "وارد و به UTC ذخیره می‌شوند.",
    saveDraft: "ذخیره پیش‌نویس",
    updateDraft: "به‌روزرسانی",
    saving: "در حال ذخیره…",
    cancel: "انصراف",
    previewTitle: "پیش‌نمایش پورتال",
    previewDescription:
      "جایگاه تبلیغات ویژه زیر فرم ورود — عرض واقعی مهمان ۳۲۰px؛ شمارش نمایش یا CPC/CPM نیست.",
    previewAria: "پیش‌نمایش صفحه ورود هات‌اسپات",
    previewViewportHint: "عرض مهمان هات‌اسپات",
    previewTagline: "خدمات اینترنت مطمئن و سریع",
    previewWelcome: "خوش آمدید",
    previewLoginHint: "برای وصل شدن به اینترنت، نام کاربری و رمز عبور خود را وارد کنید.",
    previewUsername: "نام کاربری",
    previewPassword: "رمز عبور",
    previewLoginSubmit: "ورود",
    previewEmptyHeadline: "عنوان آگهی",
    previewEmptyDescription: "توضیح کوتاه آگهی اینجا نمایش داده می‌شود.",
    previewEmptyAdvertiser: "نام تبلیغ‌کننده",
    previewEffectiveHint:
      "پیش‌نمایش مؤثر سایت در «سایت‌ها و گروه‌ها» است. این صفحه گزارش impression یا ورود تبلیغ‌کننده ندارد.",
    edit: "ویرایش",
    submitForApproval: "ارسال برای تأیید",
    publish: "نشر کردن",
    publishConfirmTitle: "تأیید نشر آگهی",
    publishConfirmLabel: "نشر نهایی",
    publishImpact:
      "پس از تأیید رمز، این آگهی حمایت‌شده برای هدف و بازهٔ زمانی تنظیم‌شده در جایگاه تبلیغات ویژه هات‌اسپات نشان داده می‌شود (الان یا طبق زمان‌بندی). یک تصویر، CTA تأییدشدهٔ HTTPS، و دسترسی پلان «تبلیغات ویژه» لازم است؛ شمارش نمایش یا CPC/CPM ثبت نمی‌شود.",
    approveAndPublish: "تأیید و نشر",
    reject: "رد",
    pause: "توقف موقت",
    pauseConfirmTitle: "تأیید توقف موقت",
    pauseConfirmLabel: "توقف موقت",
    pauseImpact:
      "نمایش این آگهی موقتاً مخفی می‌شود؛ محتوا نگه داشته می‌شود و می‌توان دوباره آن را نشان داد.",
    archive: "بایگانی کردن",
    archiveConfirmTitle: "تأیید بایگانی",
    archiveConfirmLabel: "بایگانی کردن",
    archiveImpact:
      "این آگهی دیگر در جایگاه تبلیغات ویژه هات‌اسپات نشان داده نمی‌شود؛ برای سابقه نگه داشته می‌شود.",
    resume: "ادامهٔ نمایش",
    resumeConfirmTitle: "تأیید ادامهٔ نمایش",
    resumeConfirmLabel: "ادامهٔ نمایش",
    resumeImpact:
      "آگهی دوباره طبق هدف و بازهٔ زمانی فعلی در جایگاه تبلیغات ویژه نشان داده می‌شود (در صورت واجد شرایط بودن).",
    history: "تاریخچه",
    historyTitle: "تاریخچهٔ نشر",
    historyHint: "بازگشت نسخهٔ جدید می‌سازد و تاریخچه را بازنویسی نمی‌کند.",
    historyEmpty: "نسخهٔ منتشرشده‌ای یافت نشد.",
    historyVersion: "نسخه",
    rollbackBadge: "بازگشت",
    rollbackAction: "بازگشت",
    rollbackConfirmTitleBefore: "بازگشت به نسخه",
    rollbackConfirmLabel: "تأیید بازگشت",
    rollbackImpact:
      "محتوای فعلی با این اسنپ‌شات جایگزین و به‌عنوان نسخهٔ جدید منتشر می‌شود. هدف، بازه، و قواعد فعلی CTA/تصویر همچنان اعمال می‌شوند.",
    summaryCampaign: "کمپین",
    summaryAdvertiser: "تبلیغ‌کننده",
    summaryTarget: "هدف",
    summarySchedule: "بازه",
    summaryCta: "CTA",
    rejectDialogTitle: "رد درخواست تأیید",
    rejectDialogBody:
      "به پیش‌نویس برمی‌گردد. دلیل رد در سابقه تغییرات ثبت می‌شود.",
    rejectReason: "دلیل رد",
    rejecting: "در حال رد…",
    rejectConfirm: "رد کردن",
    close: "بستن",
    loadingInline: "در حال بارگذاری…",
    draftSaved: "پیش‌نویس آگهی ذخیره شد.",
    draftUpdated: "پیش‌نویس آگهی به‌روزرسانی شد.",
    needSchedule: "شروع و پایان آگهی الزامی است.",
    needImage: "تصویر آگهی الزامی است.",
    needAdvertiser: "ابتدا یک تبلیغ‌کننده فعال انتخاب کنید.",
  },

  sponsoredUi: {
    pageSubtitle: "مرور عملیاتی آگهی‌های حمایت‌شده — متمایز از اطلاعیه‌ها و پیشنهادها",
    emptyAdvertisersTitle: "هنوز تبلیغ‌کننده‌ای ثبت نشده است",
    listTitle: "فهرست کمپین‌های تبلیغات ویژه",
    loadingDescription: "در حال دریافت کمپین‌ها",
    emptyTitle: "کمپینی ثبت نشده است",
    emptyDescription:
      "با فرم ایجاد پیش‌نویس در پایین صفحه یک کمپین بسازید. این صفحه اطلاعیه‌ها یا پیشنهادها نیست.",
    emptyDescriptionReadonly: "کمپینی برای این سازمان ثبت نشده است.",
    inspectTitle: "جزئیات کمپین",
    inspectDescription: "نام داخلی، متن مهمان، CTA، هدف و سابقه — بدون شمارش نمایش",
    previewSlotBelow: "جایگاه تبلیغات ویژه — زیر کارت ورود",
    previewSlotAria: "پیش‌نمایش جایگاه تبلیغات ویژه",
    advertisersTableAria: "جدول تبلیغ‌کنندگان",
    advertisersCardsAria: "کارت‌های تبلیغ‌کنندگان",
    colAdvertiserName: "نام نمایشی",
    colAdvertiserNotes: "یادداشت داخلی",
    searchPlaceholder: "کمپین، تبلیغ‌کننده، CTA، SITE_CODE…",
    searchAria: "جستجوی کمپین تبلیغات ویژه",
    searchHidden: "جستجوی کمپین",
    sortAria: "مرتب‌سازی فهرست تبلیغات ویژه",
    statusAria: "وضعیت کمپین",
    tableAria: "جدول تبلیغات ویژه",
    cardsAria: "کارت‌های تبلیغات ویژه",
    colStatus: "وضعیت",
    colTarget: "هدف",
    factAdvertiser: "تبلیغ‌کننده",
    factHeadline: "عنوان نمایشی",
    factDescription: "توضیح",
    factOffer: "متن پیشنهاد / قیمت",
    factSponsorshipLabel: "برچسب آگهی",
    factCta: "CTA",
    factCtaUrl: "آدرس CTA",
    factCreativePath: "مسیر خلاقه",
    factEligibility: "نمایش عمومی",
    factSubmitted: "ارسال برای تأیید",
    factPausedFrom: "توقف از",
    factExpiredWindow: "پنجره منقضی",
    inspectSectionCreative: "تبلیغ‌کننده و خلاقه",
    inspectSectionGuestCopy: "متن مهمان",
    inspectSectionPlacement: "هدف و زمان",
    inspectSectionAudit: "سابقه",
  },

  /**
   * Sites & groups (سایت‌ها و گروه‌ها) inventory, inspect, composers (V1D sites pass).
   */
  sitesUi: {
    pageSubtitle: "موجودی عملیاتی سایت‌ها — هر سایت حداکثر یک گروه عملیاتی",
    listTitle: "موجودی سایت‌ها",
    listDescriptionManage: "جستجو و بررسی وضعیت از داده‌های فعلی سازمان",
    listDescriptionReadonly: "فهرست فقط‌خواندنی سایت‌های فعال",
    emptyTitle: "سایتی ثبت نشده است",
    emptyDescriptionManage: "با فرم ایجاد سایت در پایین همین صفحه یک سایت اضافه کنید.",
    emptyDescriptionReadonly: "برای این سازمان سایت فعالی در دسترس نیست.",
    searchPlaceholder: "نام، SITE_CODE، مکان، گروه…",
    searchHidden: "جستجوی سایت",
    searchAria: "جستجوی سایت",
    sortAria: "مرتب‌سازی فهرست سایت",
    statusAria: "وضعیت سایت",
    tableAria: "جدول موجودی سایت‌ها",
    cardsAria: "کارت‌های موجودی سایت‌ها",
    colName: "نام",
    colStatus: "وضعیت",
    colGroup: "گروه",
    colSiteCode: "SITE_CODE",
    inspectTitle: "جزئیات سایت",
    inspectSectionIdentity: "هویت",
    inspectSectionMembership: "عضویت و نماینده",
    inspectSectionRelease: "عملیات مرتبط",
    factGroup: "گروه عملیاتی",
    factReseller: "نماینده",
    factLocation: "مکان",
    metricUngrouped: "بدون گروه",
    overlapAlertTitle: "هم‌پوشانی عضویت گروه",
    overlapAlertBody:
      "سایت در بیش از یک گروه است. انتخاب گروه نگه‌داشتنی الزامی است — انتخاب خودکار انجام نمی‌شود.",
    overlapApply: "اعمال نگاشت صریح",
    overlapApplying: "در حال اعمال…",
    groupsListTitle: "گروه‌های فعال",
    groupsTableAria: "جدول گروه‌های سایت",
    groupsCardsAria: "کارت‌های گروه سایت",
    colGroupName: "نام گروه",
    colMemberCount: "اعضا",
    colGroupDescription: "توضیح",
    membersUnit: "سایت",
    previewTitle: "پیش‌نمایش محتوای مؤثر",
    previewDescription: "همان قواعد سطح‌اول عمومی — بدون تغییر شکل JSON عمومی",
    previewSiteLabel: "سایت برای پیش‌نمایش",
    previewEmptySites: "سایت فعالی برای پیش‌نمایش نیست.",
    groupsReadonlyTitle: "گروه‌های سایت",
    groupsReadonlyDescription: "مدیریت گروه نیاز به مجوز groups.manage دارد",
    groupsReadonlyEmptyTitle: "فقط مشاهده",
    groupsReadonlyEmptyDescription: "برای ویرایش گروه با مدیر سازمان تماس بگیرید.",
    sitesManageReadonlyTitle: "مدیریت سایت‌ها",
    sitesManageReadonlyDescription: "نیاز به مجوز sites.manage",
    sitesManageReadonlyEmptyTitle: "ایجاد و ویرایش در دسترس نیست",
    sitesManageReadonlyEmptyDescription: "فهرست موجودی بالا فقط‌خواندنی است.",
    groupsEmptyTitle: "هنوز گروهی ثبت نشده است",
    composerModeAria: "نوع فرم ایجاد",
    composerModeSite: "سایت",
    composerModeGroup: "گروه",
    filterAll: "همه",
    filterActive: "فعال",
    filterInactive: "غیرفعال",
  },

  resellerUi: {
    pageSubtitle: "برندینگ متمرکز برای چند سایت هات‌اسپات — بدون تغییر ZIP یا روتر",
    listTitle: "موجودی پروفایل‌های نماینده",
    filterAria: "فیلتر پروفایل‌های نماینده",
    filterLogoAria: "فیلتر لوگو و اتصال (روی دادهٔ بارگذاری‌شده)",
    orgScopeLabel: "دامنه سازمان فعال",
    searchLabel: "جستجو (API)",
    searchPlaceholder: "کد یا نام…",
    searchAria: "جستجوی پروفایل نماینده",
    stateLabel: "وضعیت (API)",
    stateAria: "فیلتر وضعیت پروفایل",
    sortLabel: "مرتب‌سازی",
    sortAria: "مرتب‌سازی فهرست پروفایل‌ها",
    filterAll: "همه",
    filterHasLogo: "لوگوی اختصاصی",
    filterStagingLogo: "لوگوی موقت",
    filterFallback: "بدون لوگو / fallback",
    filterAssigned: "دارای سایت",
    filterUnassigned: "بدون سایت",
    filterChipAll: "همه",
    sortName: "نام",
    sortCode: "کد",
    sortState: "وضعیت",
    sortUpdated: "به‌روزرسانی",
    sortSites: "تعداد سایت",
    metricDraft: "پیش‌نویس",
    metricPublished: "منتشرشده",
    metricArchivedSublabel: "بایگانی",
    emptyLoadingDescription: "در حال دریافت پروفایل‌های نماینده",
    emptyTitle: "پروفایلی یافت نشد",
    emptyDescription: "جستجو یا فیلتر وضعیت API را تغییر دهید، یا پروفایل جدید بسازید.",
    filterEmptyTitle: "موردی در این فیلتر نیست",
    filterEmptyDescription: "فیلتر لوگو/اتصال را تغییر دهید.",
    inspectTitle: "جزئیات پروفایل",
    inspectDescription: "چرخه عمر، برندینگ اختصاصی در برابر fallback، و اتصال سایت",
    inspectKind: "پروفایل‌های نماینده · Reseller",
    inspectSectionIdentity: "هویت",
    inspectSectionLifecycle: "چرخه عمر",
    inspectSectionBranding: "برندینگ",
    inspectSectionAssignment: "اتصال سایت",
    inspectSectionRelated: "مسیرهای مرتبط",
    factOrg: "سازمان فعال",
    factId: "شناسه",
    factLifecycle: "وضعیت چرخه عمر",
    factBranding: "برندینگ / لوگو",
    factLogoPath: "مسیر لوگو",
    factAssignment: "اتصال سایت",
    factPublished: "انتشار",
    factUpdated: "به‌روزرسانی",
    tableAria: "جدول پروفایل‌های نماینده",
    cardsAria: "کارت‌های پروفایل نماینده",
    colLogo: "لوگو",
    colName: "نام",
    colCode: "کد",
    colStatus: "وضعیت",
    colBranding: "برندینگ",
    colSites: "سایت‌ها",
    colUpdated: "به‌روزرسانی",
    details: "جزئیات",
    safetyEyebrow: "حریم برندینگ نماینده",
    safetyBulletZip: "برندینگ مشترک چند سایت — بدون تغییر ZIP، Bundle، یا RouterOS",
    safetyBulletLogo: "بدون تصویر خارجی / CDN؛ لوگو با fallback محلی ایمن",
    safetyDisclosure: "متن کامل حریم پروفایل نماینده",
    noAccessSubtitle: "دسترسی ندارید.",
    noAccessBody: "برای مشاهدهٔ این بخش مجوز لازم نیست یا نقش شما کافی نیست.",
  },

  resellerComposer: {
    createCta: "پروفایل جدید",
    titleCreate: "ایجاد پروفایل",
    titleEdit: "ویرایش پروفایل",
    descriptionCreate: "فقط مالک می‌تواند ایجاد و ویرایش کند",
    descriptionEdit: "هویت، برندینگ ایمن، و اتصال سایت — بدون ZIP یا RouterOS",
    statusNew: "جدید",
    continueEdit: "ادامه ویرایش",
    viewForm: "مشاهده فرم",
    openSnapshots: "تاریخچه اسنپ‌شات",
    archive: "بایگانی",
    publish: "انتشار",
    close: "بستن",
    cancel: "انصراف",
    saveCreate: "ایجاد",
    saveUpdate: "ذخیره تغییرات",
    saving: "در حال ذخیره…",
    identitySection: "هویت و پشتیبانی",
    identityDescription: "کد عملیاتی پس از اولین انتشار قفل می‌شود",
    codeLabel: "کد عملیاتی",
    codeHint: "پس از اولین انتشار قفل می‌شود.",
    nameLabel: "نام عملیاتی / نمایشی",
    whatsappLabel: "واتساپ (E.164 ارقام)",
    phoneLabel: "تماس با پشتیبانی",
    phoneHint: "یک شماره — دکمهٔ تماس پورتال؛ واتساپ جدا در پشتیبانی نیست",
    hoursLabel: "ساعات پشتیبانی — اختیاری",
    supportTextLabel: "متن کوتاه پشتیبانی — اختیاری",
    taglineLabel: "شعار زیرگین (زیر لوگو) — اختیاری",
    taglineHint: "اگر خالی باشد، شعار پیش‌فرض پورتال نشان داده می‌شود.",
    termsCompanyNameLabel: "نام شرکت در شرایط استفاده — اختیاری",
    termsCompanyNameHint: "اگر خالی باشد، نام عملیاتی نمایندگی در شرایط استفاده نشان داده می‌شود.",
    brandingSection: "برندینگ و لوگو",
    brandingDescription: "پیش‌نمایش ایمن — بدون CDN خارجی یا تبدیل تصویر",
    logoUploadHelp: "JPEG، PNG یا WebP — حداکثر ۵۱۲ کیلوبایت — هر ضلع ۳۲ تا ۱۲۰۰ پیکسل",
    logoErrTooLarge: "حجم فایل بیش از ۵۱۲ کیلوبایت است.",
    logoErrInvalidType: "فقط JPEG، PNG یا WebP پذیرفته می‌شود.",
    logoErrInvalidFormat: "قالب تصویر نامعتبر است (فقط jpeg، png، webp).",
    logoErrDimensions: "ابعاد تصویر باید بین ۳۲ و ۱۲۰۰ پیکسل باشد.",
    logoErrNoFile: "فایلی برای آپلود انتخاب نشده است.",
    logoErrUnableDimensions: "خواندن ابعاد تصویر ممکن نشد.",
    logoErrGeneric: "آپلود لوگو ناموفق",
    lifecycleSection: "چرخه عمر و تاریخچه",
    lifecycleDescription: "انتشار و بایگانی بدون ZIP؛ بازگشت نسخهٔ جدید می‌سازد",
    sitesSection: "اتصال سایت‌ها",
    sitesDescription: "تغییر برندینگ نیاز به ZIP یا آپلود روتر ندارد",
  },

  bundleUi: {
    pageSubtitle:
      "ساخت ZIP اختصاصی هر سایت از الگوی تأییدشده — CMS چیزی به میکروتیک آپلود نمی‌کند",
    navGroupHint: "انتشارها",
    safetyEyebrow: "مرز اختیارات Bundle",
    safetyBulletZip: "Bundle فقط ZIP دانلودی است — نه استقرار خودکار RouterOS",
    safetyBulletRollout: "آمادهٔ دانلود ≠ تأیید rollout تولید؛ rc.21 deferred/unexecuted",
    safetyDisclosure: "متن کامل اختیارات Bundle",
    metricTemplates: "الگوها",
    metricVerified: "تأییدشده",
    metricReady: "آمادهٔ دانلود",
    metricFailedSublabel: "ناموفق",
    templateInspectTitle: "جزئیات الگو",
    templateInspectDescription: "نسخه، هش، و چرخه عمر — کپی SHA از پایین کشو",
    jobInspectTitle: "جزئیات بسته",
    jobInspectDescription: "هش کامل LTR — بدون تغییر authority استقرار",
    close: "بستن",
    details: "جزئیات",
    download: "دانلود",
    verify: "تأیید",
    retire: "بازنشسته",
    setDefault: "پیش‌فرض ساخت",
    setDefaultDone: "این الگو پیش‌فرض ساخت بسته شد.",
    noDefaultWarning:
      "هیچ الگوی تأییدشدهٔ پیش‌فرضی نیست. یک الگو را تأیید کنید یا «پیش‌فرض ساخت» را بزنید.",
    notDefaultHint: "این الگو تأیید شده اما پیش‌فرض ساخت نیست — «پیش‌فرض ساخت» را بزنید.",
    revoke: "ابطال",
    continueGenerate: "ادامه ساخت",
    noAccess: "شما مجوز دسترسی به Bundle را ندارید.",
  },

  bundleComposer: {
    generateCta: "ساخت بسته",
    importTitle: "ورود الگو",
    importDescription: "فقط Owner — ZIP تأییدشده vital-telecom",
    generateTitle: "ساخت بسته سایت",
    generateDescription: "فقط سایت‌های فعال با کد قفل‌شده",
    importSubmit: "ورود ZIP",
    importing: "در حال ورود…",
    generateSubmit: "ساخت بسته",
    generating: "در حال ساخت…",
    lastReadyTitle: "آخرین بسته آماده",
    lastReadyBody: "دانلود دستی — نه استقرار خودکار RouterOS",
  },

  sitesComposer: {
    createSiteCta: "ایجاد سایت",
    createGroupCta: "ایجاد گروه",
    groupCardTitleCreate: "ایجاد گروه سایت",
    groupCardTitleEdit: "ویرایش گروه",
    groupCardDescription: "هر سایت فقط در یک گروه عملیاتی می‌تواند عضو باشد",
    groupNameLabel: "نام گروه",
    groupDescriptionLabel: "توضیح",
    groupDescriptionOptional: "اختیاری",
    groupMembersLegend: "اعضای گروه",
    groupMembersHint: "سایت‌هایی که عضو گروه دیگرند با انتقال صریح جابه‌جا می‌شوند.",
    groupNoActiveSites: "سایت فعالی موجود نیست.",
    groupMemberInOther: "اکنون در",
    groupMemberMoveFrom: "انتقال از",
    groupMoveWarning: "جابه‌جایی سایت محتوای مؤثر گروه مبدأ/مقصد را تغییر می‌دهد.",
    groupMoveConfirm: "انتقال را تأیید می‌کنم",
    groupMoveConfirmRequired: "برای انتقال سایت از گروه دیگر، تأیید جابه‌جایی الزامی است.",
    saveGroup: "ایجاد گروه",
    updateGroup: "به‌روزرسانی",
    saving: "در حال ذخیره…",
    cancel: "انصراف",
    siteCardTitleCreate: "ایجاد سایت",
    siteCardTitleEdit: "ویرایش سایت",
    siteCardDescription: "ایجاد، ویرایش و غیرفعال‌سازی — قرارداد API بدون تغییر",
    siteCodeLabel: "کد سایت",
    siteNameLabel: "نام",
    siteLocationLabel: "مکان",
    siteLocationOptional: "اختیاری",
    saveSite: "ایجاد سایت",
    updateSite: "به‌روزرسانی سایت",
    edit: "ویرایش",
    deactivate: "غیرفعال",
    previewEffective: "پیش‌نمایش محتوای مؤثر",
    close: "بستن",
    overlapSiteLabel: "سایت",
    overlapGroupInactiveSuffix: "غیرفعال",
    siteCreated: "سایت ایجاد شد.",
    siteUpdated: "سایت به‌روزرسانی شد.",
    siteDeactivated: "غیرفعال شد.",
    groupCreated: "گروه ایجاد شد.",
    groupUpdated: "گروه به‌روزرسانی شد.",
    groupCreatedWithMoves: "گروه ایجاد و انتقال عضویت انجام شد.",
    groupUpdatedWithMoves: "گروه به‌روزرسانی و انتقال عضویت انجام شد.",
    overlapResolved: "هم‌پوشانی‌ها حل شد.",
    loadFailed: "بارگذاری ناموفق",
    saveFailed: "ذخیره ناموفق",
    previewFailed: "پیش‌نمایش ناموفق",
    actionFailed: "عملیات ناموفق",
    overlapResolveFailed: "حل هم‌پوشانی ناموفق",
    groupMemberConflict:
      "این سایت عضو گروه دیگری است. ابتدا انتقال را تأیید کنید یا از گروه قبلی خارجش کنید.",
    notFound: "یافت نشد.",
    orgRequired: "زمینهٔ سازمان انتخاب نشده است.",
    resellerDraftSuffix: "پیش‌نویس",
    resellerArchivedSuffix: "بایگانی",
  },

  settingsUi: {
    pageNote:
      "تنظیمات CMS فهرست مقصدهای HTTPS تأییدشده برای CTA و کنترل قطع فوری محتوای راه دور را نشان می‌دهد. فقط HTTPS مجاز است؛ وب‌هوک، راز، یا پیکربندی RouterOS اینجا نیست.",
    ctaAllowlistNote:
      "فقط URLهای HTTPS تأییدشده برای CTA سفارشی. مقصدهای پیش‌فرض فقط‌خواندنی‌اند و حذف نمی‌شوند. افزودن/حذف تا «ذخیره نهایی» روی سرور اعمال نمی‌شود؛ حذف فقط از فهرست تأییدشدهٔ محلی/آتی است.",
    ctaListTitle: "فهرست CTA",
    ctaListDescription:
      "مقصدهای HTTPS تأییدشده و پیش‌فرض — جستجو و فیلتر روی دادهٔ بارگذاری‌شده",
    ctaInspectTitle: "جزئیات مقصد CTA",
    ctaInspectDescription: "URL کامل، وضعیت تأیید، و اقدامات موجود",
    loadingCtaDescription: "در حال دریافت فهرست CTA",
    emptyCtaTitle: "مقصدی ثبت نشده است",
    emptyCtaDescription: "یک URL HTTPS اضافه کنید یا مقصدهای پیش‌فرض را بررسی کنید.",
    noMatchHint: "عبارت جستجو یا فیلتر نوع/وضعیت را تغییر دهید.",
    filterCtaAria: "فیلتر مقصدهای CTA",
    searchUrlPlaceholder: "جستجوی URL…",
    searchUrlHidden: "جستجوی URL",
    searchCtaAria: "جستجوی مقصدهای CTA",
    sortCtaAria: "مرتب‌سازی فهرست CTA",
    sortByUrl: "URL",
    sortByKind: "نوع",
    sortByStatus: "وضعیت",
    filterApproved: "تأییدشده",
    filterBuiltin: "پیش‌فرض",
    filterPending: "در انتظار ذخیره",
    filterSaved: "ذخیره‌شده",
    lastServerSave: "آخرین ذخیره سرور:",
    statusDestinationAria: "وضعیت مقصد",
    tableAria: "جدول مقصدهای CTA",
    cardsAria: "کارت‌های مقصد CTA",
    listContext: "زمینهٔ فهرست",
  },

  /**
   * Settings CTA allowlist editor + kill-switch confirm chrome (L4D).
   * Allowlist/HTTPS/kill semantics stay API-enforced; label stays killSwitch.title.
   */
  settingsComposer: {
    dirtyBanner:
      "تغییرات ذخیره نشده‌اند — برای اعمال روی سرور «ذخیره نهایی» را بزنید. تا آن لحظه، حذف/افزودن فقط پیش‌نویس محلی است.",
    activeBannerBefore: "فعال است — فیلدهای راه دور در API عمومی تهی می‌شوند. حداکثر انتشار کش قبلی:",
    activeBannerAfter: ".",
    httpsOnlyError: "فقط URL با HTTPS مجاز است.",
    builtinDuplicateError: "این URL از قبل به‌صورت پیش‌فرض فعال است و فقط‌خواندنی است.",
    alreadyAddedError: "این URL قبلاً اضافه شده است.",
    savedSuccess: "فهرست URLهای تأییدشده ذخیره شد.",
    killEnabledSuccess:
      "قطع فوری محتوای CMS فعال شد (فقط نمایش راه دور؛ ورود/CHAP و RouterOS دست‌نخورده).",
    killDisabledSuccess: "قطع فوری محتوای CMS غیرفعال شد.",
    killFailedFallback: "عملیات قطع فوری محتوای CMS ناموفق",
    remove: "حذف",
    saveFinal: "ذخیره نهایی",
    saving: "در حال ذخیره…",
    addUrlLabel: "افزودن URL",
    addUrlHint:
      "فقط HTTPS از فهرست تأییدشده. تا زمان «ذخیره نهایی» روی سرور اعمال نمی‌شود. مقصدهای پیش‌فرض حذف‌شدنی نیستند.",
    add: "افزودن",
    enableConfirmTitle: "فعال‌سازی قطع فوری محتوای CMS",
    disableConfirmTitle: "غیرفعال‌سازی قطع فوری محتوای CMS",
    enableConfirmLabel: "فعال کن",
    disableConfirmLabel: "غیرفعال کن",
    disableImpact:
      "پس از غیرفعال‌سازی، محتوای منتشرشدهٔ راه دور (اطلاعیه‌ها، پیشنهادها، و تبلیغات ویژه) دوباره قابل resolve است. ورود کاربران، CHAP، و فایل‌های محلی پورتال تغییر نمی‌کنند.",
    enableButton: "فعال‌سازی قطع فوری محتوای CMS",
    disableButton: "غیرفعال‌سازی",
    reasonOptional: "دلیل (اختیاری)",
    reasonPlaceholder: "مثلاً قطع اضطراری نمایش",
    ownerOnlyHint: "فعال/غیرفعال کردن فقط برای مالک پلتفرم مجاز است.",
    killKind: "کنترل ایمنی سراسری",
    platformWide: "سراسری پلتفرم",
    factReason: "دلیل",
    factFrom: "از",
    factExpires: "انقضا",
    factActor: "فعال‌کننده",
    factCache: "Cache-Control",
    factMaxPropagation: "حداکثر انتشار کش قبلی",
  },

  observability: {
    privacyKind: "حریم خصوصی",
    privacyHeading: "شمارنده‌های بدون PII",
    privacyBanner:
      "شمارنده‌ها و هشدارها فاقد نام کاربری، آدرس MAC، اثرانگشت دستگاه، IP، User-Agent، دادهٔ مرور، اعتبارنامه، دادهٔ نشست، یا لاگ خام حساس هستند.",
    noLiveOpsNote:
      "این صفحه پیکربندی زندهٔ وب‌هوک/اسکرپر انجام نمی‌دهد و مقصد هشدار یا توکن را نمایش نمی‌دهد. فعال‌سازی عملیاتی خارج از UI است.",
    /**
     * Operator-facing aggregate disclaimer (Dari).
     * Replaces the former full English explanatory sentence in the UI.
     * API may still return an English contract field for tests — do not render it.
     */
    aggregateDisclaimer:
      "شمارنده‌های تقریبی عملیاتی هستند (کش، پاسخ ۳۰۴، ربات‌ها، کلید اضطراری). معیار بازدید تأییدشده یا CPM نیستند.",
    /** Must not reappear as operator-visible helper copy. */
    deprecatedAggregateDisclaimerEn:
      "Approximate operational counts (cache, 304, bots, kill switch). Not viewability or CPM.",
  },

  /**
   * Shared V1D-A composer chrome labels (presentation only).
   * Field labels and lifecycle copy remain on L4A–L4C composer keys.
   */
  composerChrome: {
    kindAnnouncement: "اطلاعیه",
    kindPromotion: "پیشنهاد",
    kindSponsored: "تبلیغات ویژه",
    kindAdvertiser: "تبلیغ‌کننده",
    kindSite: "سایت",
    kindSiteGroup: "گروه سایت",
    kindOrganization: "حساب سازمان",
    kindReseller: "پروفایل نماینده",
    kindBundle: "Bundle",
    kindBundleImport: "ورود الگو",
    kindBundleGenerate: "ساخت بسته",
    statusCreate: "ایجاد",
    statusEditDraft: "ویرایش پیش‌نویس",
    statusLocked: "قفل‌شده",
    sectionBasic: "محتوای پایه",
    sectionTargeting: "هدف‌گذاری",
    sectionSchedule: "زمان‌بندی",
    sectionCtaImage: "فراخوان و تصویر",
    sectionEmergency: "اضطراری",
    sectionLifecycle: "چرخهٔ عمر",
    sectionPreview: "پیش‌نمایش",
    sectionAdvertiser: "تبلیغ‌کننده",
    /** Single targeting + timezone hint for composer headers (no duplicated Asia/Kabul). */
    contextSummary: "هدف‌گذاری سایت / گروه / سازمان · زمان‌بندی Asia/Kabul",
  },

  /** English technical tokens intentionally retained in UI. */
  technical: {
    Bundle: "Bundle",
    Pilot: "Pilot",
    Canary: "Canary",
    API: "API",
    Backup: "Backup",
    RouterOS: "RouterOS",
    SITE_CODE: "SITE_CODE",
    CTA: "CTA",
    MFA: "MFA",
    CHAP: "CHAP",
  },
} as const;

export type CmsStrings = typeof cmsStrings;

/** Nested string values of the catalog (for typing helpers). */
export type CmsStringValue = {
  [K in keyof CmsStrings]: CmsStrings[K] extends string
    ? CmsStrings[K]
    : CmsStrings[K][keyof CmsStrings[K]];
}[keyof CmsStrings];
